import {
  ReferAndEarnReferralsProps,
  ReferAndEarnSettingsProps,
} from "@/@types/ReferAndEarn";
import Users from "@/assets/icons/Users";
import Sucess from "@/assets/icons/Sucess";
import MouseClick from "@/assets/mouse-click.svg";
import { GenericModal } from "@/components/Onboarding/GenericModal";
import CardCurrency from "@/components/shared/CardCurrency/CardCurrency";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { AuthService } from "@/services/auth.service";
import { ReferAndEarnService } from "@/services/refer-and-earn.service";
import useCurrencyStore from "@/store/useCurrencyStore";
import useModalStore from "@/store/useModalStore";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import WithdrawalForm from "../../../Financial/Partials/WithdrawalForm";
import { usePermissions } from "@/auth/PermissionsContext";

export default function PlatformReferralsTab() {
  const { t } = useTranslation(["referAndEarn", "wallet"]);
  const [isCopied, setIsCopied] = useState(false);
  const [page, setPage] = useState(1);
  const { modals, openModal, closeModal } = useModalStore();
  const { hasPermission, isPrimary } = usePermissions();

  const canViewReferralLink = hasPermission('referral.view_referral_link');
  const canRequestReferralWithdrawal =
    isPrimary && hasPermission('wallet.request_referral_withdrawal');

  const currency = useCurrencyStore((s) => s.currency);

  const { data: userInfo, isPending: isLoadingUserInfo } = useQuery({
    queryKey: ["user-info"],
    queryFn: async () => await AuthService.userInfo(),
  });

  const { data: settings, isPending: isLoadingSettings } =
    useQuery<ReferAndEarnSettingsProps>({
      queryKey: ["refer-and-earn-settings", currency],
      queryFn: async () => await ReferAndEarnService.getSettings(currency),
    });

  const { data: referrals, isPending: isLoadingReferrals } =
    useQuery<ReferAndEarnReferralsProps>({
      queryKey: ["refer-and-earn-referrals", page, currency],
      queryFn: async () => await ReferAndEarnService.getReferrals(page, currency),
    });

  const hasWithdrawableBalance =
    Number(settings?.withdrawal?.available_balance_withdrawal ?? 0) > 0;
  const isWithdrawalDisabled =
    isLoadingUserInfo || isLoadingSettings || !hasWithdrawableBalance;

  const valueToCopy = `${window.location.origin}/register?src=mundpay&ref=${userInfo?.reference}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(valueToCopy).then(() => {
      setIsCopied(true);
      toast({ description: t("copy_link_success"), variant: "success" });
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const headers = [
    { title: t("headers.user"), key: "user" },
    { title: t("headers.commission"), key: "commission" },
    { title: t("headers.registration_date"), key: "registration_date" },
  ];

  return (
    <>
      {canRequestReferralWithdrawal && (
        <CustomSheet
          open={modals["withdrawal-form-referral"]}
          onClose={() => closeModal("withdrawal-form-referral")}
        >
          <WithdrawalForm
            key={`withdrawal-referral-${currency}`}
            enabled={Boolean(modals["withdrawal-form-referral"])}
            availableBalance={settings?.withdrawal?.available_balance_withdrawal_formatted ?? ''}
            availableBalanceCents={Math.round(
              Number(settings?.withdrawal?.available_balance_withdrawal ?? 0) * 100,
            )}
            currency={currency}
            type="referral"
            onClose={() => closeModal("withdrawal-form-referral")}
          />
        </CustomSheet>
      )}

      <GenericModal
        modalKey="withdrawal-registered"
        title={t("wallet:withdrawalRegistered.title")}
        description={`${t("wallet:withdrawalRegistered.line1")}\n\n${t(
          "wallet:withdrawalRegistered.line2"
        )}\n\n${t("wallet:withdrawalRegistered.line3")}`}
        confirmButtonText={t("wallet:withdrawalRegistered.confirmButtonText")}
        cancelButtonText={t("wallet:withdrawalRegistered.confirmButtonText")}
        icon={<Sucess />}
        hideImage
        hideCancelButton
      />

      <section className="my-8 max-w-lg m-auto space-y-6 flex flex-col items-center">
        <img
          className="w-10 h-10 object-contain"
          src={MouseClick}
          alt={"img"}
        />
        <h1 className="w-3/4 text-xl font-semibold text-center">
          {t("description", {
            percentage: settings?.data?.commission_percentage_formatted,
            months: settings?.data?.expiration_months,
          })}
        </h1>
        {canViewReferralLink && (
          <div className="space-y-1">
            <div className="relative">
              <Input
                type="text"
                value={valueToCopy}
                readOnly
                className="pr-10 text-primary"
              />
              <Button
                onClick={handleCopy}
                size="sm"
                variant="ghost"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-2"
              >
                {isCopied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <span className="text-muted text-xs">{t("agree_terms")}</span>
          </div>
        )}
      </section>

      <section className="flex w-full gap-6 flex-col md:flex-row mt-6">
        <CardCurrency
          text={t("available")}
          value={settings?.withdrawal?.available_formatted ?? ''}
          status="paid"
          isLoading={isLoadingUserInfo || isLoadingSettings}
          button={
            canRequestReferralWithdrawal ? (
              isWithdrawalDisabled ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex max-xl:w-full">
                      <Button className="max-xl:w-full" disabled>
                        {t("request_withdrawal")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("wallet:withdrawal_validation.no_balance")}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  className="max-xl:w-full"
                  onClick={() => {
                    openModal("withdrawal-form-referral");
                  }}
                >
                  {t("request_withdrawal")}
                </Button>
              )
            ) : undefined
          }
        />
        <CardCurrency
          text={t("total_withdrawn")}
          value={settings?.withdrawal?.withdrawal_formatted ?? ''}
          isLoading={isLoadingSettings}
          status="period"
        />
      </section>

      <Card className="w-full mt-6 overflow-hidden bg-[#18181B]">
        <div className="p-4 flex items-center gap-4">
          <h2 className="text-base font-medium">{t("my_referrals")}</h2>
          <Badge
            variant="secondary"
            className="text-xs font-medium gap-2 flex items-center justify-center"
          >
            Total: {referrals?.meta?.total}
          </Badge>
        </div>

        <TableListCustom
          headers={headers}
          isLoadingData={isLoadingReferrals}
          data={referrals ?? { data: [] }}
          emptyIconMessage={<Users />}
          emptyTitleMessage={t("no_referrals")}
          emptyDescriptionMessage={t("invite_friends")}
          currentPage={page}
          setCurrentPage={setPage}
        >
          {referrals?.data?.map((referral) => (
            <TableRow
              key={referral.id}
              className="text-sm border-t border-zinc-800"
            >
              <TableCell>
                <p className="text-colorText">{referral.referred.name}</p>
                <p className="text-muted">{referral.referred.email}</p>
              </TableCell>
              <TableCell>
                <p>{referral.total_commission_distributed_formatted}</p>
              </TableCell>
              <TableCell>
                <p className="text-muted">
                  {referral.referred.created_at_formatted}
                </p>
              </TableCell>
            </TableRow>
          ))}
        </TableListCustom>
      </Card>
    </>
  );
}
